import { useEffect, useLayoutEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { useNavigate } from 'react-router-dom'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { TOPICS, topicLabel } from '@gogrov/shared'
import { supabase } from '@/lib/supabase'
import { useEffectiveClientId } from '@/hooks/useAuth'
import { usePlan, useRegions, useAddRegion, useRemoveRegion, useCompleteOnboarding } from '@/hooks/useConfig'
import { useSaveMix } from '@/hooks/useMix'
import { useUpdateProfile, useUpdatePrefs } from '@/hooks/useProfile'
import {
  usePlaceSearch, usePlaceNear, useDefaultRadius, useHomeParents, useWideScopes, useCountryRegions, usePinPlace, useNearPrefs,
} from '@/hooks/usePlaceCatalogue'
import {
  RADII, isLocal, isWide, countryWord, regionScope, langList, langName, placeWords,
  type CataloguePlace, type NearPlace,
} from '@/lib/placeCatalogue'
import { setLocale } from '@/lib/i18n'
import { TopicGlyph } from '@/components/gg/TopicGlyph'
import { BrandMark } from '@/components/gg/Shell'
import { Emma, emmaEngine } from '@/components/emma/Emma'
import { VoiceInterview } from '@/components/onboarding/VoiceInterview'
import { useVoiceInterview } from '@/components/onboarding/useVoiceSeeding'
import { PlaceRadar } from '@/components/onboarding/PlaceRadar'
import { Think, type ThinkLine } from '@/components/onboarding/Think'
import {
  PICKS, GREETS, LOCALE_NAME, CONTINUE, BOTH_LINE, deviceLang, hello, BIZ, PLATS, LAUNCH_LANGS, CLASSES, classOf, BIZ_CLASS,
  ABROAD_FIRST, classTopics, mixFromClass, evenMix, setShare, allWide, CAPS, POSITIONS, defaultPos, SAMPLE, ORDER, SECS,
  type LangPick, type UiLang, type MixRow, type Position, type Step,
} from '@/components/onboarding/onboardingData'
import '@/styles/gg.css'
import '@/styles/onboarding2.css'
import '@/styles/onboarding3.css'

/**
 * Onboarding v2 (Brian's after/onboarding.html v2.12, Oct 30 to 31): the setup a new account goes through once, on the
 * website after sign-up (the iPhone app shows "Finish setting up on the website"). Emma guides it one question per
 * screen, like a phone's setup assistant: Howdy (your language: English, Español or Bilingual), hello, your name, yourself
 * or a business and where you post, languages, your class (the topics as a character sheet), the topics, the place (or
 * the world), Emma breaking ground there (the dial: the radius, the border switch, the places next door), a look at the
 * mix, a position per topic, the voice seeding (VoiceInterview: the starter five), Emma growing the account, and a look
 * at the first brief. Back on every screen after Hello; forward once a screen has been passed. Everything is saved as it
 * goes (clients.onboarding), so a closed tab picks up where it stopped. Setup is free; Open my brief finishes it.
 */

type Kind = '' | 'creator' | 'business'
type Borders = 'home' | 'open'
type Held = Pick<CataloguePlace, 'id' | 'name' | 'kind' | 'region' | 'country' | 'st'>

interface OnbState {
  step: Step
  seen: Step[]
  pick: LangPick
  name: string
  handle: string
  kind: Kind
  biz: string
  plats: string[]
  langs: UiLang[]
  cls: string
  clsApplied: string
  topics: string[]
  mix: MixRow[]
  pos: Record<string, Position>
  home: CataloguePlace | null
  radius: number | null
  borders: Borders
  near: Held[]
}

const START: OnbState = {
  step: 'greet', seen: [], pick: deviceLang(), name: '', handle: '', kind: '', biz: '', plats: ['Instagram'], langs: [],
  cls: '', clsApplied: '', topics: [], mix: [], pos: {}, home: null, radius: null, borders: 'home', near: [],
}

const TOPIC = Object.fromEntries(TOPICS.map((t) => [t.id, t]))
const label = (id: string) => TOPIC[id]?.label ?? topicLabel(id)
const reduced = () => typeof window !== 'undefined' && !!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches
const CK = <svg className="ck" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M3 8.5l3.2 3L13 4.5" /></svg>

/** Emma's line arrives a word at a time, the way a setup assistant writes its headline in. */
function Words({ children, className }: { children: string | string[]; className?: string }) {
  const lines = Array.isArray(children) ? children : [children]
  let k = 0
  return (
    <h1 className={`ob-h1${className ? ` ${className}` : ''}`} aria-label={lines.join(' ')}>
      {lines.map((line, li) => (
        <span key={li} className="ob-line" aria-hidden="true">
          {line.split(/(\s+)/).map((w, i) => /^\s+$/.test(w) ? w : w ? <span key={i} className="w" style={{ animationDelay: `${160 + (k++) * 70}ms` }}>{w}</span> : null)}
        </span>
      ))}
    </h1>
  )
}

function useClientUpdate() {
  const clientId = useEffectiveClientId()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (patch: Record<string, unknown>) => {
      const { error } = await supabase.from('clients').update(patch).eq('id', clientId!)
      if (error) throw error
    },
    onSuccess: (_d, patch) => {
      if (!('onboarding' in patch && Object.keys(patch).length === 1)) {
        qc.invalidateQueries({ queryKey: ['client-prefs', clientId] })
        qc.invalidateQueries({ queryKey: ['near-prefs', clientId] })
      }
    },
  })
}

export function Onboarding() {
  const navigate = useNavigate()
  const { data: plan } = usePlan()
  const { data: regions } = useRegions()
  const { data: nearPrefs, isLoading: prefsLoading } = useNearPrefs()
  const { data: interview } = useVoiceInterview()
  const updateClient = useClientUpdate()
  const addRegion = useAddRegion()
  const removeRegion = useRemoveRegion()
  const pin = usePinPlace()
  const saveMix = useSaveMix()
  const complete = useCompleteOnboarding()
  const updateProfile = useUpdateProfile()
  const updateUserPrefs = useUpdatePrefs()

  const [A, setA] = useState<OnbState>(START)
  const [hist, setHist] = useState<Step[]>([])
  const [toastMsg, setToastMsg] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const resumed = useRef(false)
  const set = (patch: Partial<OnbState>) => setA((a) => ({ ...a, ...patch }))
  const toast = (m: string) => { setToastMsg(m); window.setTimeout(() => setToastMsg((x) => (x === m ? null : x)), 2600) }

  const PLAN = { name: plan?.name ?? 'Sprout', max_topics: plan?.max_topics ?? 5, max_regions: plan?.max_regions ?? 5 }

  // Resume where they stopped (clients.onboarding), once.
  useEffect(() => {
    if (resumed.current || prefsLoading) return
    resumed.current = true
    const saved = (nearPrefs?.onboarding ?? {}) as Partial<OnbState>
    if (saved.step && (ORDER as readonly string[]).includes(saved.step)) {
      setA({ ...START, ...saved } as OnbState)
      setHist(ORDER.slice(0, ORDER.indexOf(saved.step)) as Step[])
    }
  }, [prefsLoading, nearPrefs])

  // Save as it goes.
  useEffect(() => {
    if (!resumed.current) return
    const t = window.setTimeout(() => updateClient.mutate({ onboarding: A }), 500)
    return () => window.clearTimeout(t)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [A])

  const step = A.step
  function show(s: Step, back = false) {
    setError(null)
    if (!back) setHist((h) => [...h, A.step])
    setA((a) => ({ ...a, step: s, seen: a.seen.includes(a.step) ? a.seen : [...a.seen, a.step] }))
    window.scrollTo({ top: 0 })
    window.setTimeout(() => emmaEngine()?.ask(), 300)
  }
  const nextOf = (s: Step) => ORDER[ORDER.indexOf(s) + 1] as Step
  const next = () => show(nextOf(step))
  const goBack = () => { const h = [...hist]; const prev = h.pop(); if (!prev) return; setHist(h); show(prev, true) }

  // ---------------- 0 · Howdy ----------------
  // The carousel takes turns until they pick English or Español; Bilingual keeps it going (v2.12).
  const [greetI, setGreetI] = useState(0)
  const pickedRef = useRef(false)
  useEffect(() => {
    if (step !== 'greet' || reduced() || (pickedRef.current && A.pick !== 'both')) return
    let t = window.setTimeout(function tick() {
      if (!document.hidden) setGreetI((i) => (i + 1) % GREETS.length)
      t = window.setTimeout(tick, 2600)
    }, greetI === 0 ? 2800 : 2600)
    return () => window.clearTimeout(t)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, A.pick])
  function pickLang(p: LangPick) {
    pickedRef.current = true
    set({ pick: p })
    if (p !== 'both') setGreetI(GREETS.findIndex((g) => g[1] === (p === 'es' ? 'es-US' : 'en-US')))
  }
  function leaveGreet() {
    const langs: UiLang[] = A.pick === 'both' ? ['en', 'es'] : [A.pick]
    const language = A.pick === 'both' ? 'bilingual' : A.pick
    setLocale(A.pick === 'es' ? 'es-US' : 'en-US')
    updateClient.mutate({ language, locale: A.pick === 'es' ? 'es-US' : 'en-US' })
    set({ langs: A.langs.length ? A.langs : langs })
    next()
  }

  // ---------------- 2 · Name, 3 · Kind ----------------
  function leaveName() {
    const first = A.name.trim().split(/\s+/)[0]
    set({ name: first })
    updateProfile.mutate({ first_name: first })
    if (A.handle.trim()) updateUserPrefs.mutate({ display_handle: A.handle.trim().slice(0, 40) })
    next()
  }
  const kindOk = !!A.kind && (A.kind === 'creator' || !!A.biz) && A.plats.length > 0
  function leaveKind() {
    updateClient.mutate({ account_kind: A.kind, business_type: A.kind === 'business' ? A.biz : null })
    next()
  }

  // ---------------- 3b · Languages ----------------
  function leaveLangs() {
    const language = A.langs.includes('en') && A.langs.includes('es') ? 'bilingual' : A.langs[0] ?? 'en'
    updateClient.mutate({ language })
    next()
  }

  // ---------------- 5a · Class ----------------
  const abroadHome = (A.home?.country ?? 'United States') !== 'United States'
  const classList = abroadHome ? ABROAD_FIRST.map((id) => classOf(id)!) : CLASSES
  useEffect(() => {
    if (step === 'cls' && !A.cls && A.kind === 'business') set({ cls: BIZ_CLASS[A.biz] ?? 'local' })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step])
  function leaveCls() {
    if (!A.cls) return
    if (A.clsApplied !== A.cls) {
      const topics = classTopics(A.cls, PLAN.max_topics)
      setA((a) => ({ ...a, clsApplied: a.cls, topics: [], mix: mixFromClass(a.cls, topics), pos: {}, home: allWide(topics) !== allWide(a.topics) ? null : a.home }))
      setTopicsFresh(true)
    }
    next()
  }

  // ---------------- 6 · Topics ----------------
  const [topicsFresh, setTopicsFresh] = useState(false)
  const [topicsReady, setTopicsReady] = useState(false)
  const [showAll, setShowAll] = useState(false)
  const [justPicked, setJustPicked] = useState<string[]>([])
  const suggested = useMemo(() => classTopics(A.cls || 'local', PLAN.max_topics), [A.cls, PLAN.max_topics])
  useEffect(() => { if (step === 'topics') setTopicsReady(!topicsFresh && A.topics.length > 0) }, [step]) // eslint-disable-line react-hooks/exhaustive-deps
  async function plantTopics() {
    setTopicsReady(true)
    const rm = reduced()
    for (let i = 0; i < suggested.length; i++) {
      await new Promise((r) => window.setTimeout(r, rm ? 20 : 260))
      const id = suggested[i]
      setA((a) => (a.topics.includes(id) ? a : { ...a, topics: [...a.topics, id] }))
      setJustPicked((j) => [...j, id])
    }
    setTopicsFresh(false)
    setA((a) => ({ ...a, mix: mixFromClass(a.cls, a.topics) }))
  }
  function toggleTopic(id: string) {
    setA((a) => {
      if (a.topics.includes(id)) { const topics = a.topics.filter((t) => t !== id); return { ...a, topics, mix: mixFromClass(a.cls, topics) } }
      if (a.topics.length >= PLAN.max_topics) { toast(`${PLAN.name} holds ${PLAN.max_topics} topics. Take one off to add this.`); return a }
      const topics = [...a.topics, id]
      return { ...a, topics, mix: mixFromClass(a.cls, topics) }
    })
  }
  const tiles = useMemo(() => {
    const first = new Set(suggested)
    if (showAll) return TOPICS.map((t) => t.id)
    const lead = TOPICS.filter((t) => first.has(t.id) || A.topics.includes(t.id)).sort((a, b) => Number(first.has(b.id)) - Number(first.has(a.id))).map((t) => t.id)
    return lead.concat(TOPICS.filter((t) => !first.has(t.id) && !A.topics.includes(t.id)).slice(0, 7).map((t) => t.id))
  }, [suggested, showAll, A.topics])

  // ---------------- 4 · Place ----------------
  const wide = allWide(A.topics)
  const wideNames = A.topics.filter(isWide).map(label)
  const [q, setQ] = useState('')
  const [hi, setHi] = useState(0)
  const homeCountry = 'United States'   // launch is US-only (v2.12); the locale's country steers the search
  const { data: found, isFetching: searching } = usePlaceSearch(q, { limit: 6, prefer: homeCountry })
  const { data: scopes } = useWideScopes(homeCountry)
  const PW = placeWords('US')
  function pickHome(p: CataloguePlace) {
    set({ home: p, near: [], radius: null })
    setQ(`${p.name}, ${p.region}`)
    window.setTimeout(() => show('connect'), 260)
  }
  const covWord = (p: CataloguePlace) => p.kind === 'city' || p.kind === 'county' ? (p.news_only ? 'News' : 'News and records')
    : p.kind === 'state' || p.kind === 'province' ? `The whole ${p.kind}` : p.kind === 'region' ? 'A region' : p.kind === 'country' ? 'The whole country' : 'News'

  // ---------------- 5 · Connect: Emma breaks ground ----------------
  const home = A.home
  const local = isLocal(home)
  const scopeKind = home?.kind === 'continent' || home?.kind === 'world'
  const only = A.borders === 'home'
  const { data: defR } = useDefaultRadius(local ? home : null, only)
  const radius = local ? (A.radius ?? defR ?? null) : null
  const { data: nearData, isFetching: nearLoading } = usePlaceNear(home && !scopeKind ? home : null, radius, only, 8)
  const { data: parents } = useHomeParents(local ? home : null)
  const { data: homeScopes } = useWideScopes(home?.country ?? homeCountry)
  const { data: countryRegions } = useCountryRegions(home?.kind === 'country' ? home.country : null, home?.kind === 'country' && home.country !== 'United States')
  const [connectRun, setConnectRun] = useState(0)
  const [connectStarted, setConnectStarted] = useState(false)
  const [connectDone, setConnectDone] = useState(false)
  const [lit, setLit] = useState(0)
  const [labels, setLabels] = useState(false)
  useEffect(() => { if (step === 'connect') { setConnectStarted(false); setConnectDone(false); setLit(0); setLabels(false); setConnectRun((n) => n + 1) } }, [step, A.home?.id]) // eslint-disable-line react-hooks/exhaustive-deps
  const nb: NearPlace[] = nearData?.cities ?? []
  const chips: Held[] = home?.kind === 'country' && home.country !== 'United States' ? (countryRegions ?? []).slice(0, 8)
    : [...nb, ...(parents?.county ? [parents.county] : []), ...(parents?.state ? [parents.state] : [])]
  const C = homeScopes?.national
  const cname = countryWord(home?.kind === 'country' ? home.name : home?.country)
  const spoken: string[] = scopeKind ? A.langs : (C?.languages ?? [])
  const inLang = spoken.length ? ` in ${langList(spoken.slice(0, 2))}` : ''
  const nRec = !!home && !home.news_only
  const watch = !!home?.watch
  const connectReady = !!home && (scopeKind || (!nearLoading && (!local || radius != null)))
  useEffect(() => { if (step === 'connect' && connectReady && !connectStarted) setConnectStarted(true) }, [step, connectReady, connectStarted])
  const missingLang = home && !scopeKind && home.country !== homeCountry && spoken[0] && (LAUNCH_LANGS as string[]).includes(spoken[0]) && !A.langs.includes(spoken[0] as UiLang) ? spoken[0] as UiLang : null
  const borderOffers = useMemo(() => {
    const seen = new Set<string>()
    return nb.filter((x) => x.country && x.country !== home?.country && !seen.has(x.country) && seen.add(x.country))
  }, [nb, home])
  const connectLines: ThinkLine[] = !home ? [] : [
    {
      say: scopeKind ? <>Setting your reach to <b>{home.kind === 'world' ? 'the whole world' : home.name}</b>…</> : <>Finding <b>{home.name}</b> on the map…</>,
      res: scopeKind ? `${A.topics.map(label).slice(0, 3).join(', ')}, from everywhere`
        : home.kind === 'country' ? (home.continent ?? '') : home.kind === 'region' ? `${C?.region_word ? `A ${C.region_word} of ` : ''}${cname}`
        : home.region + (cname && home.country !== 'United States' && !home.region.endsWith(home.country ?? '') ? `, ${cname}` : ''),
      ms: 900,
    },
    {
      say: scopeKind ? `Turning over the news${inLang}…` : `Turning over the local news that covers it${inLang}…`,
      res: scopeKind ? 'The outlets and the voices people actually follow on your topics' : 'The outlets, the city pages, the groups people actually read',
      ms: 1500, on: () => setLit(Math.ceil(nb.length / 2)), after: () => setLit(nb.length),
    },
    {
      say: 'Testing the soil: which public records can I read every week?', soft: !nRec, ms: 1300,
      res: nRec ? 'Licenses, permits and meeting agendas, every Monday.' : scopeKind ? 'Records are a town thing. Add a home town any time and I’ll read its records too.' : `Not outside the US yet. The news${inLang} covers it, and I’ll say so on every story.`,
    },
    {
      say: 'Can I keep watch over it for breaking news?', soft: !watch, ms: 1100,
      res: scopeKind ? 'Yes. The minute something breaks on your topics, anywhere, you’ll know.' : watch ? `Yes. I can tell you the minute something breaks in ${home.name}.` : 'Not here yet. Your brief still covers it every morning.',
    },
    {
      say: scopeKind ? 'Anything local in there?' : home.kind === 'country' ? `Who’s around ${cname}?` : radius ? `Peeking over the fence: who’s within ${radius} miles?` : 'Peeking over the fence: who’s next door?',
      res: scopeKind ? 'Not yet. Your topics reach wide; a home town is one tap away below.'
        : nb.length ? nb.slice(0, 5).map((x) => `${x.name} ${x.miles} mi ${x.dir}${x.country && x.country !== home.country ? ` (${x.country})` : ''}`).join(', ') + (home.kind === 'region' || nb.length > 5 ? '…' : '')
        : 'I’ll find neighbors as coverage grows.',
      ms: 1000, on: () => setLabels(true),
    },
  ]
  function toggleNear(x: Held) {
    setA((a) => {
      if (a.near.some((n) => n.id === x.id)) return { ...a, near: a.near.filter((n) => n.id !== x.id) }
      if (a.near.length >= PLAN.max_regions - 1) { toast(`${PLAN.name} covers ${PLAN.max_regions} places. Swap one, or move up a plan later.`); return a }
      return { ...a, near: [...a.near, { id: x.id, name: x.name, kind: x.kind, region: x.region, country: x.country, st: x.st }] }
    })
  }
  const toggleLang = (c: UiLang) => setA((a) => ({ ...a, langs: a.langs.includes(c) ? (a.langs.length > 1 ? a.langs.filter((x) => x !== c) : a.langs) : [...a.langs, c] }))

  /** The places become the account's regions: home first, then the ones next door (free while setting up). */
  async function leaveConnect() {
    if (!home) return
    setBusy(true); setError(null)
    try {
      for (const r of regions ?? []) await removeRegion.mutateAsync(r.id)
      const want: Held[] = [home, ...A.near.filter((n) => n.id !== home.id)].slice(0, PLAN.max_regions)
      for (const p of want) {
        const id = await addRegion.mutateAsync({ name: p.name, scope: regionScope(p) })
        await pin.mutateAsync({ regionId: id, placeId: p.id }).catch(() => null)
      }
      const st = home.st && home.st.length === 2 ? home.st : null
      updateClient.mutate({
        city_state: home.kind === 'city' || home.kind === 'county' ? `${home.name}${st ? `, ${st}` : ''}` : home.kind === 'world' ? 'The whole world' : home.name,
        ...(st ? { state: st } : {}),
        near_radius: local ? radius : null, near_borders: A.borders,
        language: A.langs.includes('en') && A.langs.includes('es') ? 'bilingual' : A.langs[0] ?? 'en',
      })
      set({ radius: local ? radius : null })
      next()
    } catch (e) { setError((e as Error).message) } finally { setBusy(false) }
  }

  // ---------------- 7 · The mix: a tour, then yours ----------------
  const [cap, setCap] = useState(-1)
  const [finger, setFinger] = useState<{ x: number; y: number; on: boolean; press: boolean }>({ x: 0, y: 0, on: false, press: false })
  const demoOn = useRef(false)
  const tourRef = useRef<HTMLDivElement>(null)
  useEffect(() => {
    if (step !== 'mix') return
    setA((a) => (a.mix.length && a.mix.map((r) => r.t).join() === a.topics.join() ? a : { ...a, mix: a.cls ? mixFromClass(a.cls, a.topics) : evenMix(a.topics) }))
    if (reduced()) { setCap(3); return }
    let alive = true
    demoOn.current = true
    const wait = (ms: number) => new Promise((r) => window.setTimeout(r, ms))
    const ok = () => alive && demoOn.current
    const xy = (sel: string, frac?: number): [number, number] | null => {
      const tour = tourRef.current?.getBoundingClientRect(), el = tourRef.current?.querySelector(sel)?.getBoundingClientRect()
      if (!tour || !el) return null
      const tw = 28
      return frac == null ? [el.left - tour.left + el.width / 2, el.top - tour.top + el.height / 2]
        : [el.left - tour.left + tw / 2 + (el.width - tw) * frac / 100, el.top - tour.top + el.height / 2]
    }
    let cur: MixRow[] = []
    setA((a) => { cur = a.mix; return a })
    const glide = async (i: number, to: number, ms: number) => {
      const from = cur[i]?.v ?? 0, n = Math.max(8, Math.round(ms / 30))
      for (let k = 1; k <= n; k++) {
        if (!ok()) return false
        const e = 0.5 - 0.5 * Math.cos((Math.PI * k) / n)
        cur = setShare(cur, i, from + (to - from) * e)
        setA((a) => ({ ...a, mix: cur }))
        const p = xy(`[data-r="${i}"]`, cur[i].v); if (p) setFinger({ x: p[0], y: p[1], on: true, press: true })
        await wait(30)
      }
      return true
    }
    void (async () => {
      await wait(1100); if (!ok()) return
      setCap(0); await wait(1500); if (!ok()) return
      const b = Math.min(2, cur.length - 1)
      let p = xy('[data-r="0"]', cur[0]?.v ?? 0); if (p) setFinger({ x: p[0], y: p[1], on: true, press: false })
      await wait(600); if (!ok()) return
      setCap(1); if (!(await glide(0, Math.min((cur[0]?.v ?? 0) + 22, 60), 1300))) return
      await wait(700); if (!ok()) return
      setCap(2)
      p = xy(`[data-lk="${b}"]`); if (p) setFinger({ x: p[0], y: p[1], on: true, press: false })
      await wait(600); if (!ok()) return
      if (p) setFinger({ x: p[0], y: p[1], on: true, press: true })
      cur = cur.map((r, j) => (j === b ? { ...r, lock: true } : r)); setA((a) => ({ ...a, mix: cur }))
      await wait(500); if (!ok()) return
      p = xy('[data-r="0"]', cur[0].v); if (p) setFinger({ x: p[0], y: p[1], on: true, press: false })
      await wait(600); if (!ok()) return
      if (!(await glide(0, Math.max(cur[0].v - 30, 8), 1300))) return
      await wait(600); if (!ok()) return
      cur = evenMix(cur.map((r) => r.t)); setA((a) => ({ ...a, mix: cur }))
      setFinger((f) => ({ ...f, on: false })); demoOn.current = false; setCap(3)
    })()
    return () => { alive = false; demoOn.current = false }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step])
  function stopDemo() {
    if (!demoOn.current) return
    demoOn.current = false
    setFinger((f) => ({ ...f, on: false }))
    setA((a) => ({ ...a, mix: a.mix.map((r) => ({ ...r, lock: false })) }))
    setCap(3)
  }
  function toggleLock(i: number) {
    const r = A.mix[i]
    if (!r.lock && A.mix.filter((x) => !x.lock).length <= 2) { toast('Two topics have to stay free to move.'); return }
    set({ mix: A.mix.map((x, j) => (j === i ? { ...x, lock: !x.lock } : x)) })
  }

  // ---------------- 8 · Positions ----------------
  const [pi, setPi] = useState(0)
  const [swap, setSwap] = useState(false)
  useEffect(() => { if (step === 'positions') setPi(Math.max(0, A.topics.findIndex((t) => !A.pos[t]))) }, [step]) // eslint-disable-line react-hooks/exhaustive-deps
  const allPos = A.topics.every((t) => A.pos[t])
  const goPos = (i: number) => { setSwap(true); window.setTimeout(() => { setPi(i); setSwap(false) }, 220) }
  function choosePos(v: Position) {
    const id = A.topics[pi]
    const pos = { ...A.pos, [id]: v }
    set({ pos })
    emmaEngine()?.ask()
    const nx = A.topics.findIndex((t, i) => i > pi && !pos[t])
    if (nx >= 0) window.setTimeout(() => goPos(nx), 420)
  }
  /** The first mix goes in (free while setting up), each topic at its position. */
  async function saveTopics(pos: Record<string, Position>) {
    setBusy(true); setError(null)
    try {
      const mix = A.mix.length && A.mix.map((r) => r.t).join() === A.topics.join() ? A.mix : mixFromClass(A.cls, A.topics)
      await saveMix.mutateAsync(mix.map((r) => ({ topic: r.t, share: r.v, locked: false, voice: pos[r.t] ?? defaultPos(A.kind) })))
      set({ pos })
      next()
    } catch (e) { setError((e as Error).message) } finally { setBusy(false) }
  }
  function leavePositions() {
    const nx = A.topics.findIndex((t) => !A.pos[t])
    if (nx >= 0) { goPos(nx); return }
    void saveTopics(A.pos)
  }
  function decideLater() {
    const pos = { ...A.pos }
    A.topics.forEach((t) => { if (!pos[t]) pos[t] = defaultPos(A.kind) })
    void saveTopics(pos)
  }

  // ---------------- 12 · Build ----------------
  const [meter, setMeter] = useState(0)
  const answered = (interview?.answers ?? []).filter((a) => !a.skipped && a.a?.trim()).length
  const places = home ? [home.name, ...A.near.map((n) => n.name)] : []
  const buildLines: ThinkLine[] = useMemo(() => {
    const L: ThinkLine[] = [{ say: `Hooking up the local news across ${places.length === 1 ? places[0] : `${places.length} places`}…`, ms: 1300 }]
    if (home && !home.news_only) L.push({ say: 'Setting up your Monday public records check…', ms: 1000 })
    if (home?.watch) L.push({ say: `Setting a watch over ${home.name} for breaking news…`, ms: 900 })
    L.push({ say: `Laying out your rows: ${A.mix.slice(0, 3).map((r) => `${label(r.t)} ${r.v}%`).join(', ')}${A.mix.length > 3 ? '…' : ''}`, ms: 1100 })
    L.push({ say: `Seeding your voice into the scripts, from ${answered} answer${answered === 1 ? '' : 's'}…`, ms: 1500 })
    L.push({ say: `Shaping scripts for ${A.plats.slice(0, 3).join(', ')}${A.langs.length > 1 ? `, in ${langList(A.langs)}` : ''}…`, ms: 900 })
    L.push({ say: 'Churning through today’s news for your first brief…', ms: 1500 })
    return L.map((l, i, all) => ({ ...l, after: () => setMeter((i + 1) / all.length) }))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step === 'build'])
  useEffect(() => { if (step === 'build') setMeter(0) }, [step])

  // ---------------- 13 · Done ----------------
  async function openBrief() {
    setBusy(true); setError(null)
    try {
      updateClient.mutate({ onboarding: { ...A, step: 'done', at: new Date().toISOString() } })
      await complete.mutateAsync()
      navigate('/app', { replace: true })
    } catch (e) { setError((e as Error).message); setBusy(false) }
  }

  // ---------------- the foot, per screen ----------------
  type Foot = { label?: string; disabled?: boolean; hide?: boolean; alt?: string; onAlt?: () => void; go?: () => void }
  const H = hello(A.pick)
  const foot: Foot = (() => {
    switch (step) {
      case 'greet': return { label: CONTINUE[A.pick], go: leaveGreet }
      case 'hello': return { label: H.btn, go: next }
      case 'name': return { disabled: !A.name.trim(), go: leaveName }
      case 'kind': return { disabled: !kindOk, go: leaveKind }
      case 'langs': return { disabled: !A.langs.length, go: leaveLangs }
      case 'cls': return { disabled: !A.cls, go: leaveCls }
      case 'topics': return topicsReady ? { disabled: A.topics.length < 2, alt: A.topics.length < 2 ? 'Pick at least two' : '', go: next } : { hide: true }
      case 'place': return { disabled: !A.home, go: () => show('connect') }
      case 'connect': return connectDone ? { disabled: busy, label: busy ? 'Saving' : 'Continue', go: () => void leaveConnect() } : { hide: true }
      case 'mix': return { go: () => { stopDemo(); next() } }
      case 'positions': return { label: busy ? 'Saving' : allPos ? 'Continue' : 'Next topic', disabled: busy || !A.pos[A.topics[pi]], alt: allPos ? '' : 'Decide later', onAlt: decideLater, go: leavePositions }
      case 'voice': return { hide: true }
      case 'build': return { hide: true }
      case 'done': return { label: busy ? 'Opening' : 'Open my brief', disabled: busy, go: () => void openBrief() }
    }
  })()
  const nx = nextOf(step)
  const canFwd = !!nx && A.seen.includes(nx) && !['build', 'done', 'connect', 'voice'].includes(step) && !foot.disabled && !foot.hide
  const backOn = hist.length > 0 && !['build', 'done'].includes(step)
  const emmaMode = step === 'greet' || step === 'done' || step === 'voice' ? 'hide' : step === 'hello' || step === 'build' ? 'big' : 'small'
  const secOn = SECS.find(([, list]) => list.includes(step))?.[0]

  // Enter moves on, as on a setup assistant (not while typing in a field).
  useEffect(() => {
    const k = (e: KeyboardEvent) => {
      if (e.key !== 'Enter' || (e.target as HTMLElement)?.matches?.('input, textarea, button, select')) return
      if (!foot.disabled && !foot.hide) foot.go?.()
    }
    document.addEventListener('keydown', k)
    return () => document.removeEventListener('keydown', k)
  })

  const fitRef = useRef<HTMLSpanElement>(null)
  useLayoutEffect(() => {
    const sp = fitRef.current, box = sp?.parentElement
    if (!sp || !box) return
    sp.style.fontSize = ''
    const room = box.clientWidth - 56, w = sp.scrollWidth
    if (w > room && room > 0) sp.style.fontSize = `${Math.floor(parseFloat(getComputedStyle(sp).fontSize) * room / w)}px`
  }, [greetI, step])

  let screen: ReactNode = null
  switch (step) {
    case 'greet': {
      const [w, loc] = GREETS[greetI]
      screen = (
        <div className="ob-col ob-center">
          <div className="ob-hello" aria-hidden="true"><span key={greetI} ref={fitRef} className="hw">{w}</span></div>
          <div className="ob-hello-of" key={`of${greetI}`}>{LOCALE_NAME[loc]}</div>
          <div className="ob-rise">
            <div className="ob-lab">Pick your language · Elige tu idioma</div>
            <div className="ob-langs" role="radiogroup" aria-label="Your language">
              {PICKS.map(([c, nat, other]) => (
                <button key={c} type="button" role="radio" aria-checked={A.pick === c} className={A.pick === c ? 'on' : ''} onClick={() => pickLang(c)}>
                  {nat}<small>{other}</small>
                </button>
              ))}
            </div>
            <p className="ob-small">{BOTH_LINE[A.pick]}</p>
          </div>
        </div>
      )
      break
    }
    case 'hello':
      screen = (
        <div className="ob-col ob-center">
          <Words>{H.head}</Words>
          <p className="ob-lede">{H.lede}</p>
        </div>
      )
      break
    case 'name':
      screen = (
        <div className="ob-col">
          <Words>First, what should I call you?</Words>
          <p className="ob-lede">This is how GoGrov greets you. Your handle is where your posts go.</p>
          <div className="ob-rise">
            <div className="ob-field"><input id="obName" autoComplete="given-name" placeholder=" " maxLength={40} value={A.name} autoFocus
              onChange={(e) => set({ name: e.target.value })} onKeyDown={(e) => { if (e.key === 'Enter' && A.name.trim()) leaveName() }} /><label htmlFor="obName">Your first name</label></div>
            <div className="ob-field"><input id="obHandle" autoComplete="off" placeholder=" " maxLength={40} value={A.handle}
              onChange={(e) => set({ handle: e.target.value })} onKeyDown={(e) => { if (e.key === 'Enter' && A.name.trim()) leaveName() }} /><label htmlFor="obHandle">Your handle or page name (optional)</label></div>
          </div>
        </div>
      )
      break
    case 'kind':
      screen = (
        <div className="ob-col">
          <Words>{`Nice to meet you, ${A.name || 'there'}.`}</Words>
          <p className="ob-lede">Are you posting as yourself, or for a business? A business gets the line its posts need, like a license number.</p>
          <div className="ob-rise">
            <div className="ob-cards two">
              <button type="button" className={`ob-card${A.kind === 'creator' ? ' on' : ''}`} aria-pressed={A.kind === 'creator'} onClick={() => set({ kind: 'creator', biz: '' })}>
                <b>As myself</b><span>A local creator: what’s happening and what you think of it.</span></button>
              <button type="button" className={`ob-card${A.kind === 'business' ? ' on' : ''}`} aria-pressed={A.kind === 'business'} onClick={() => set({ kind: 'business' })}>
                <b>For a business</b><span>Real estate, lending, tourism, a shop. Posts carry your line.</span></button>
            </div>
            {A.kind === 'business' && (
              <>
                <div className="ob-lab">What kind of business</div>
                <div className="ob-chips">{BIZ.map((b) => <button key={b} type="button" className={`ob-chip${A.biz === b ? ' on' : ''}`} aria-pressed={A.biz === b} onClick={() => set({ biz: b })}>{CK}{b}</button>)}</div>
              </>
            )}
            <div className="ob-lab">Where you post</div>
            <div className="ob-chips">
              {PLATS.map((p) => {
                const on = A.plats.includes(p)
                return <button key={p} type="button" className={`ob-chip${on ? ' on' : ''}`} aria-pressed={on} onClick={() => set({ plats: on ? A.plats.filter((x) => x !== p) : [...A.plats, p] })}>{CK}{p}</button>
              })}
            </div>
            <p className="ob-small">Scripts and captions come shaped for the places you pick. Change them any time.</p>
          </div>
        </div>
      )
      break
    case 'langs':
      screen = (
        <div className="ob-col">
          <Words>Which languages do you post in?</Words>
          <p className="ob-lede">Your scripts and captions come in each one you pick.</p>
          <div className="ob-rise">
            <div className="ob-chips">{LAUNCH_LANGS.map((c) => <button key={c} type="button" className={`ob-chip${A.langs.includes(c) ? ' on' : ''}`} aria-pressed={A.langs.includes(c)} onClick={() => toggleLang(c)}>{CK}{langName(c)}</button>)}</div>
            <p className="ob-small">Post in both? Pick both. The second one gets its own voice questions later, so you sound like you in both.</p>
          </div>
        </div>
      )
      break
    case 'cls':
      screen = (
        <div className="ob-col">
          <Words>What kind of creator are you?</Words>
          <p className="ob-lede">Pick the one closest. It sets your starting topics, like a character’s stats. You tune them next.</p>
          <div className="ob-classes ob-rise">
            {classList.map((c) => {
              const top = Object.entries(c.stats).sort((a, b) => b[1] - a[1]).slice(0, 4), max = top[0][1]
              return (
                <button key={c.id} type="button" className={`ob-cls${A.cls === c.id ? ' on' : ''}`} aria-pressed={A.cls === c.id} onClick={() => { set({ cls: c.id }); emmaEngine()?.ask() }}>
                  <b>{c.name}</b><span>{c.line}</span>
                  {top.map(([t, v]) => (
                    <div key={t} className="stat"><TopicGlyph topic={t} size={20} tone={A.cls === c.id ? 'amber' : 'cream'} /><span>{label(t)}</span><i><b style={{ width: `${Math.round((v / max) * 100)}%` }} /></i><em>{v}</em></div>
                  ))}
                </button>
              )
            })}
          </div>
        </div>
      )
      break
    case 'topics': {
      const c = classOf(A.cls)
      screen = (
        <div className="ob-col">
          <Words>Here’s what I’d plant first.</Words>
          {!topicsReady && suggested.length >= 2 && (
            <Think key="topics" onDone={() => void plantTopics()} lines={[
              { say: c ? <>Reading your sheet: <b>{c.name}</b>…</> : 'Thinking about what a local creator posts…', res: `${label(suggested[0])} and ${label(suggested[1])} lead it`, ms: 1200 },
              { say: `What else a ${(c?.name ?? 'local').toLowerCase()} creator posts…`, res: suggested.slice(2).map(label).join(', '), ms: 1000 },
              { say: `Fitting it to ${PLAN.name}…`, res: PLAN.name === 'Sprout' ? `${suggested.length} to plant. Sprouts start small.` : `${suggested.length} to plant, room for ${PLAN.max_topics}`, ms: 800 },
            ]} />
          )}
          {topicsReady && (
            <>
              <div className="ob-cap"><span>Tap to change. <b>{A.topics.length}</b> of <b>{PLAN.max_topics}</b> on {PLAN.name}.</span>
                <button type="button" className="ob-link" onClick={() => setShowAll((s) => !s)}>{showAll ? 'Show fewer' : `See all ${TOPICS.length}`}</button></div>
              <div className="ob-tiles">
                {tiles.map((id, i) => {
                  const on = A.topics.includes(id)
                  return (
                    <button key={id} type="button" className={`ob-tile${on ? ' on' : ''}${justPicked.includes(id) ? ' pick' : ''}`} style={{ animationDelay: `${40 + i * 45}ms` }}
                      aria-pressed={on} onClick={() => toggleTopic(id)}>
                      <span className="tick" /><TopicGlyph topic={id} size={36} tone={on ? 'amber' : 'cream'} /><b>{label(id)}</b>
                    </button>
                  )
                })}
              </div>
            </>
          )}
        </div>
      )
      break
    }
    case 'place': {
      const results = q.trim().length >= 2 && !(A.home && q === `${A.home.name}, ${A.home.region}`) ? found ?? [] : null
      const picks = wide && scopes ? [scopes.national, scopes.world, ...(scopes.continents ?? [])].filter(Boolean) as CataloguePlace[] : []
      screen = (
        <div className="ob-col">
          <Words>{wide ? 'Where in the world?' : 'Where do you cover?'}</Words>
          <p className="ob-lede">
            {wide
              ? `${wideNames.length > 2 ? `${wideNames.slice(0, 2).join(', ')} and the rest` : wideNames.join(' and ')} aren’t tied to a town. Pick a country, a continent or the whole world, and I’ll pull from there. You can add a home town any time.`
              : wideNames.length ? `Start with home: the town your followers know you for. ${wideNames.join(' and ')} cover the world wherever you are; the rest comes from your town.`
              : `Start with home: the town your followers know you for. ${PW[0]}, or anywhere else by name. You can add the places around it next.`}
          </p>
          <div className="ob-rise">
            {picks.length > 0 && (
              <div className="ob-chips">
                {picks.map((x) => <button key={x.id} type="button" className={`ob-chip${A.home?.id === x.id ? ' on' : ''}`} aria-pressed={A.home?.id === x.id}
                  onClick={() => { set({ home: x, near: [], radius: null }); setQ('') }}>{CK}{x.kind === 'world' ? 'The whole world' : x.name}</button>)}
              </div>
            )}
            <div className="ob-field">
              <input id="obPlace" autoComplete="off" placeholder=" " role="combobox" aria-expanded={!!results} aria-controls="obResults" value={q} autoFocus={!wide}
                onChange={(e) => { setQ(e.target.value); setHi(0); if (A.home && !wide) set({ home: null }) }}
                onKeyDown={(e) => {
                  if (!results?.length) return
                  if (e.key === 'ArrowDown' || e.key === 'ArrowUp') { e.preventDefault(); setHi((h) => (h + (e.key === 'ArrowDown' ? 1 : results.length - 1)) % results.length) }
                  if (e.key === 'Enter') { e.preventDefault(); pickHome(results[hi]) }
                }} />
              <label htmlFor="obPlace">{wide ? 'Or search a country or region' : PW[1]}</label>
            </div>
            {results && (
              <div className="ob-results" id="obResults" role="listbox">
                {results.length ? results.map((p, i) => (
                  <button key={p.id} type="button" role="option" aria-selected={i === hi} className={i === hi ? 'hi' : ''} onClick={() => pickHome(p)}>
                    <span><b>{p.name}</b><small>{p.region}</small></span><em>{covWord(p)}</em>
                  </button>
                )) : !searching && <div className="none">GoGrov doesn’t cover “{q.trim()}” yet. Try the nearest city, the county, the state or the country.</div>}
              </div>
            )}
          </div>
        </div>
      )
      break
    }
    case 'connect': {
      if (!home) { screen = null; break }
      const nbLab = nearData?.siblings ? (home.kind === 'country' ? `Add a ${C?.region_word || 'region'} of ${cname}` : `Add more of ${cname}`)
        : local && radius ? `Add the places within ${radius} miles${(nearData?.total ?? 0) > 8 ? ` (the ${nb.length} nearest of ${nearData?.total})` : ''}` : 'Add the places next door'
      screen = (
        <div className="ob-col">
          <Words>{scopeKind ? `Setting your reach: ${home.name}.` : `Breaking ground in ${home.name}.`}</Words>
          {!scopeKind && <PlaceRadar home={home} near={nb} radius={radius} lit={lit} labels={labels} still={connectDone} />}
          {connectDone && local && (
            <div className="ob-radii" role="radiogroup" aria-label="Within">
              <small>Within</small>
              {RADII.map((r) => <button key={r} type="button" role="radio" aria-checked={r === radius} className={r === radius ? 'on' : ''} onClick={() => { set({ radius: r }); emmaEngine()?.ask() }}>{r} mi</button>)}
            </div>
          )}
          {connectDone && local && (nearData?.abroad || !only) && (
            <div className="ob-radii" role="radiogroup" aria-label="Borders">
              <small>Borders</small>
              <button type="button" role="radio" aria-checked={only} className={only ? 'on' : ''} onClick={() => set({ borders: 'home' })}>Only {countryWord(home.country)}</button>
              <button type="button" role="radio" aria-checked={!only} className={only ? '' : 'on'} onClick={() => set({ borders: 'open' })}>Across borders</button>
            </div>
          )}
          {connectStarted
            ? <Think key={`c${connectRun}`} lines={connectLines} onDone={() => { setConnectDone(true); setLit(nb.length); setLabels(true) }} />
            : <div className="ob-think"><div className="tl in"><span className="st" /><p>Finding <b>{home.name}</b> on the map…</p></div></div>}
          {connectDone && (
            <div className="ob-after">
              <div className="ob-disc">
                {([
                  ['yes', 'Local news', spoken.length ? `Every morning, in ${langList(spoken.slice(0, 2))}` : 'Every morning'],
                  [nRec ? 'yes' : 'no', 'Public records', nRec ? 'Available' : scopeKind ? 'With a home town' : 'Not outside the US yet'],
                  [watch ? 'yes' : 'no', 'Breaking watch', watch ? 'Available' : 'Not here yet'],
                  ['yes', 'Your plan', `${PLAN.name}: up to ${PLAN.max_regions} places`],
                ] as const).map(([c2, b, s]) => <div key={b} className={c2}><b>{b}</b><span>{s}</span></div>)}
              </div>
              {missingLang && (
                <>
                  <div className="ob-lab">They speak {langName(missingLang)} in {cname}</div>
                  <div className="ob-chips"><button type="button" className={`ob-chip${A.langs.includes(missingLang) ? ' on' : ''}`} onClick={() => toggleLang(missingLang)}>{CK}Add {langName(missingLang)} to my scripts</button></div>
                </>
              )}
              {borderOffers.length > 0 && (
                <>
                  <div className="ob-lab">Across the border</div>
                  <div className="ob-chips">{borderOffers.map((x) => <span key={x.country} className="ob-note">{x.name} is in {x.country}. The brief reads its news in its own language.</span>)}</div>
                </>
              )}
              {chips.length > 0 && (
                <>
                  <div className="ob-lab">{nbLab}</div>
                  <div className="ob-chips">
                    {chips.map((x) => {
                      const on = A.near.some((n) => n.id === x.id)
                      const m = x as Partial<NearPlace>
                      return (
                        <button key={x.id} type="button" className={`ob-chip${on ? ' on' : ''}`} aria-pressed={on} onClick={() => toggleNear(x)}>
                          {CK}{x.name}{m.miles != null && <small>{m.miles} mi {m.dir}{x.country && x.country !== home.country ? ` · ${x.country}` : ''}</small>}
                        </button>
                      )
                    })}
                  </div>
                  <p className="ob-small">Add up to {PLAN.max_regions - 1} more now, or later in Feed controls › Places.</p>
                </>
              )}
            </div>
          )}
        </div>
      )
      break
    }
    case 'mix':
      screen = (
        <div className="ob-col">
          <Words>Your mix, at a glance.</Words>
          <p className="ob-lede">Each topic gets a share of your brief. No need to set it now. Watch how it moves.</p>
          <div className="ob-tour ob-rise" ref={tourRef} onPointerDownCapture={stopDemo}>
            <div className="sum"><span>Your brief</span><span><b>{A.mix.reduce((s, r) => s + r.v, 0)}</b>%</span></div>
            {A.mix.map((r, i) => (
              <div key={r.t} className={`mrow${r.lock ? ' locked' : ''}`}>
                <TopicGlyph topic={r.t} size={26} tone="amber" />
                <div><div className="nm">{label(r.t)}</div>
                  <input className="rng" type="range" min={0} max={100} step={1} value={r.v} data-r={i} disabled={r.lock} aria-label={`${label(r.t)} share`}
                    style={{ ['--v' as string]: `${r.v}%` }} onChange={(e) => set({ mix: setShare(A.mix, i, +e.target.value) })} /></div>
                <span className="val">{r.v}%</span>
                <button type="button" className={`lk${r.lock ? ' on' : ''}`} data-lk={i} aria-label={`${r.lock ? 'Unlock' : 'Lock'} ${label(r.t)}`} aria-pressed={r.lock} onClick={() => toggleLock(i)}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" aria-hidden="true"><rect x="5" y="11" width="14" height="10" rx="2" /><path d={r.lock ? 'M8 11V8a4 4 0 0 1 8 0v3' : 'M8 11V8a4 4 0 0 1 7.5-2'} /></svg>
                </button>
              </div>
            ))}
            <div className={`finger${finger.on ? ' on' : ''}${finger.press ? ' press' : ''}`} style={{ left: finger.x, top: finger.y }} />
          </div>
          <div className="ob-caption" key={cap}>{cap >= 0 ? CAPS[cap] : ''}</div>
          <div className="ob-steps3">{CAPS.map((_, k) => <i key={k} className={k === cap ? 'on' : ''} />)}</div>
        </div>
      )
      break
    case 'positions': {
      const id = A.topics[pi]
      screen = (
        <div className="ob-col">
          <Words>One quick question per topic.</Words>
          <p className="ob-lede">I write every story from one of three positions. Your answer sets where each topic starts.</p>
          {id && (
            <div className={`ob-rise ob-posfade${swap ? ' swap' : ''}`}>
              <div className="ob-ptop"><TopicGlyph topic={id} size={44} tone="amber" /><div><small>{pi + 1} of {A.topics.length}</small><b>{label(id)}</b></div></div>
              <div className="ob-pos">
                {POSITIONS.map((o) => (
                  <button key={o.v} type="button" className={`ob-card${A.pos[id] === o.v ? ' on' : ''}`} aria-pressed={A.pos[id] === o.v} onClick={() => choosePos(o.v)}>
                    <div><b>{o.said}</b><span>{o.what}</span></div><em>{o.name}</em>
                  </button>
                ))}
              </div>
            </div>
          )}
          <div className="ob-ptabs">{A.topics.map((t, i) => <i key={t} className={`${A.pos[t] ? 'on' : ''}${i === pi ? ' cur' : ''}`} />)}</div>
        </div>
      )
      break
    }
    case 'voice':
      screen = (
        <div className="ob-col ob-voice">
          <Words>Now let’s seed your voice.</Words>
          <VoiceInterview topics={A.topics} onBack={goBack} onFinish={() => show('build')} finishing={false} doneLabel="Build my GoGrov" />
        </div>
      )
      break
    case 'build':
      screen = (
        <div className="ob-col">
          <Words className="ob-center">{`Growing GoGrov for ${A.name || 'you'}.`}</Words>
          <div className="ob-meter"><b style={{ width: `${Math.round(meter * 100)}%` }} />
            <img className="sprout" src="/icons/icon.svg" alt="" style={{ left: `${Math.round(meter * 100)}%`, transform: `translate(-50%, -100%) scale(${(0.35 + 0.65 * meter).toFixed(2)})` }} /></div>
          <Think key="build" lines={buildLines} onDone={() => window.setTimeout(() => show('done'), 500)} />
        </div>
      )
      break
    case 'done':
      screen = (
        <div className="ob-col ob-center">
          <div className="ob-mark"><BrandMark /></div>
          <Words>{`Your first harvest is in, ${A.name || 'friend'}.`}</Words>
          <p className="ob-lede">Your first brief is ready: stories from {home ? (home.kind === 'world' ? 'the whole world' : home.name) : 'your town'}, grown from your answers. Here’s a look.</p>
          <div className="ob-brief">
            {A.topics.slice(0, 3).map((id, i) => (
              <div key={id} style={{ animationDelay: `${500 + i * 160}ms` }}>
                <TopicGlyph topic={id} size={26} tone="amber" />
                <span><small>{label(id)}</small><b>{SAMPLE[id] ?? `Today’s ${label(id).toLowerCase()} story`}</b></span>
              </div>
            ))}
          </div>
        </div>
      )
      break
  }

  return (
    <div className={`gg ob${step === 'greet' ? ' is-greet' : ''}${['greet', 'hello', 'connect', 'build', 'done'].includes(step) ? ' is-warm' : ''}`} data-theme="dark">
      <div className="ob-aura" aria-hidden="true" />
      <div className="ob-glow" aria-hidden="true"><i /><i /></div>

      <header className="ob-top">
        <button type="button" className={`ob-back${backOn ? ' on' : ''}`} aria-label="Back" onClick={goBack} disabled={!backOn}>
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="M15 5l-7 7 7 7" /></svg>
        </button>
        <nav className={`ob-secs${secOn ? ' on' : ''}`} aria-label="Setup progress">
          {SECS.map(([name, list]) => {
            const i = list.indexOf(step), all = ORDER.indexOf(step), last = ORDER.indexOf(list[list.length - 1])
            const w = all > last ? 100 : i >= 0 ? Math.round(((i + 1) / (list.length + 1)) * 100) : 0
            return <span key={name} className={name === secOn ? 'on' : ''}><i><b style={{ width: `${w}%` }} /></i>{name}</span>
          })}
        </nav>
        <button type="button" className={`ob-back ob-fwd${canFwd ? ' on' : ''}`} aria-label="Forward" disabled={!canFwd} onClick={() => foot.go?.()}>
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="M9 5l7 7-7 7" /></svg>
        </button>
      </header>

      <div className={`ob-emma ob-emma--${emmaMode}`} aria-hidden="true"><Emma variant="sheet" /></div>

      <main className={`ob-scr ob-scr--${step}${emmaMode === 'big' ? ' big' : ''}`} key={step}>
        {screen}
        {error && <p className="gg-error ob-col">{error}</p>}
      </main>

      {!foot.hide && (
        <div className="ob-foot">
          <button type="button" className="ob-go" disabled={foot.disabled} onClick={() => foot.go?.()}>{foot.label ?? 'Continue'}</button>
          {foot.alt ? <button type="button" className="ob-link" onClick={foot.onAlt} disabled={!foot.onAlt}>{foot.alt}</button> : null}
        </div>
      )}
      <div className={`ob-toast${toastMsg ? ' on' : ''}`} role="status">{toastMsg}</div>
    </div>
  )
}
